/*
  Care & Guarantee — progressive diagnostic flowchart + animated section counters.
  Adapted from a standalone HTML export (Claude Projects/sub pages/final pages/
  care-and-guarantee.html) for sections/care-and-guarantee.liquid.

  Two changes from the original export:
  1. syncSidebar() measures the theme's header and exposes its CURRENT bottom
     edge as --rd-care-header-h, so the fixed sidebar sits just below it. This
     is tracked continuously (every scroll frame), not just once, because the
     theme's header hides itself on scroll-down (slides off via transform)
     without shrinking its own layout height — a one-time height measurement
     would leave a permanent gap the size of the header above the sidebar
     even after the header has scrolled out of view.
  2. The same function toggles a "released" class once less than a viewport's
     worth of this page's own content remains below — see the CSS for what
     that switches the sidebar to. Fixed elements otherwise have no awareness
     of where the section they belong to actually ends, so without this the
     sidebar would float on top of the theme's footer once scrolled past.
*/
(() => {
  const wrapper = document.querySelector('.rd-care');

  function syncSidebar(){
    const header = document.querySelector('.header-wrapper');
    if (!wrapper) return;
    let ticking = false;
    const apply = () => {
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      wrapper.style.setProperty('--rd-care-header-h', `${headerBottom}px`);

      const vh = window.innerHeight;
      const released = wrapper.getBoundingClientRect().bottom <= vh;
      wrapper.classList.toggle('released', released);

      ticking = false;
    };
    apply();
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(apply); }
    }, { passive: true });
    window.addEventListener('resize', apply);
    if (header && 'ResizeObserver' in window) {
      new ResizeObserver(apply).observe(header);
    }
  }
  syncSidebar();

  // ---- progressive, self-building diagnostic flowchart ----
  const FLOW = {
    q1:{ q:"Is there visible soil or mud on the garment?",
      opts:[ {label:"Yes",next:"r_s3"}, {label:"No",next:"q2"} ] },
    q2:{ q:"Does it pass the sniff test?",
      opts:[ {label:"Pass",next:"q3"}, {label:"Fail",next:"q2b"} ] },
    q2b:{ q:"Is it general staleness, or a deep-set odour?",
      opts:[ {label:"General staleness",next:"r_s4"}, {label:"Deep odour",next:"r_s3"} ] },
    q3:{ q:"Are surface pills or bobbles visible?",
      opts:[ {label:"Yes",next:"r_s6"}, {label:"No",next:"q4"} ] },
    q4:{ q:"Is it being prepped for seasonal storage?",
      opts:[ {label:"Yes",next:"r_s7"}, {label:"No",next:"r_ok"} ] },
    r_s3:{ result:true, sec:"s3", code:"03", title:"The Gentle Soak", desc:"A full system flush is required to clear soil or deep-set odour." },
    r_s4:{ result:true, sec:"s4", code:"04", title:"The Fresh Air Fix", desc:"General staleness clears with 12–24 hours of airflow — no wash needed." },
    r_s6:{ result:true, sec:"s6", code:"06", title:"Pill Patrol", desc:"Use the knitwear comb at 45° to lift surface pills." },
    r_s7:{ result:true, sec:"s7", code:"07", title:"Deep Freeze Protocol", desc:"Seal and freeze to terminate moth larvae before storage." },
    r_ok:{ result:true, sec:null, title:"Mission Ready", desc:"Diagnostics passed on all counts. Return the garment to its drawer — no servicing required." }
  };

  const flow = document.getElementById('flow');
  let seq = 0;

  function reveal(node){ requestAnimationFrame(()=>node.classList.add('in')); }
  function focusNode(node){ setTimeout(()=>node.scrollIntoView({behavior:'smooth',block:'center'}),120); }

  function addArrow(){
    const a=document.createElement('div');
    a.className='fc-arrow reveal';
    a.innerHTML='<div class="line"></div>';
    flow.appendChild(a); reveal(a);
  }

  function addQuestion(id){
    seq++;
    const node=FLOW[id];
    const card=document.createElement('div');
    card.className='fc-card q reveal';
    card.innerHTML='<div class="fc-badge">Decision '+String(seq).padStart(2,'0')+'</div>'+
      '<div class="fc-q">'+node.q+'</div><div class="fc-opts"></div>';
    const opts=card.querySelector('.fc-opts');
    node.opts.forEach(o=>{
      const b=document.createElement('button');
      b.type='button'; b.className='fc-opt';
      b.innerHTML='<span>'+o.label+'</span><span class="oarrow">&rarr;</span>';
      b.onclick=()=>answer(card,o);
      opts.appendChild(b);
    });
    flow.appendChild(card); reveal(card);
    return card;
  }

  function answer(card,opt){
    card.classList.add('answered');
    card.querySelector('.fc-opts').innerHTML='<span class="fc-chosen">Answer <b>'+opt.label+'</b></span>';
    addArrow();
    const next=FLOW[opt.next];
    const node = next.result ? addResult(opt.next) : addQuestion(opt.next);
    focusNode(node);
  }

  function addResult(id){
    const node=FLOW[id];
    const card=document.createElement('div');
    card.className='fc-card result reveal'+(node.sec?'':' ok');
    let inner;
    if(node.sec){
      inner='<a class="fc-result-link" href="#'+node.sec+'">'+
        '<div class="fc-badge">Recommended Procedure</div>'+
        '<div class="fc-result-title">Proceed to Section '+node.code+' — '+node.title+' <span class="go-arrow">&rarr;</span></div>'+
        '<div class="fc-result-desc">'+node.desc+'</div></a>';
    } else {
      inner='<div class="fc-result-link" style="cursor:default;">'+
        '<div class="fc-badge">Diagnostic Complete</div>'+
        '<div class="fc-result-title">'+node.title+'</div>'+
        '<div class="fc-result-desc">'+node.desc+'</div></div>';
    }
    card.innerHTML=inner+'<button type="button" class="fc-restart">Restart Diagnostic</button>';
    card.querySelector('.fc-restart').onclick=reset;
    flow.appendChild(card); reveal(card);
    return card;
  }

  function reset(){
    flow.innerHTML=''; seq=0;
    const start=document.createElement('div');
    start.className='fc-card start reveal in';
    start.textContent='START · Garment has been worn';
    flow.appendChild(start);
    addArrow();
    addQuestion('q1');
  }

  reset();

  // ---- animated count-up for the section index numbers ----
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const indices = document.querySelectorAll('.sec-index');

  function countUp(el, target){
    const dur = 650, start = performance.now();
    function frame(now){
      const t = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const val = Math.round(eased * target);
      el.textContent = String(val).padStart(2,'0') + ' /';
      if(t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  if(indices.length){
    if(reduceMotion || !('IntersectionObserver' in window)){
      // leave numbers as-is
    } else {
      const io = new IntersectionObserver((entries)=>{
        entries.forEach(e=>{
          if(e.isIntersecting && !e.target.dataset.done){
            e.target.dataset.done = '1';
            const m = e.target.textContent.match(/\d+/);
            const target = m ? parseInt(m[0],10) : 0;
            countUp(e.target, target);
            io.unobserve(e.target);
          }
        });
      }, {threshold:.6});
      indices.forEach(el=>io.observe(el));
    }
  }
})();
